import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Card } from "../../ui/Card";
import Title from "../../ui/Title";
import { StyledInput } from "../../ui/StyledInput";
import { ChangeEvent, useEffect, useState } from "react";
import usePincode from "../../../hooks/usePincode";
import { OrderFormType } from "./AddOrder";
import { StyledButton } from "../../ui/StyledButton";

const BuyerDetailsForm = ({
  label,
  handleNext,
  setFinalForm,
  finalForm,
}: {
  label: string;
  handleNext: () => void;
  setFinalForm: (finalForm: any) => void;
  finalForm: OrderFormType;
}) => {
  const [pincode, setPincode] = useState<string>(
    finalForm.receiver_address.postal_code
  );

  const [billingPincode, setBillingPincode] = useState<string>(
    typeof finalForm.billing_address === "object"
      ? finalForm.billing_address.postal_code
      : ""
  );

  const { city, state } = usePincode(pincode);
  const { city: billingCity, state: billingState } = usePincode(billingPincode);

  const onlyNumber = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    limit: number
  ) => event.target.value.replace(/[^0-9]/g, "").slice(0, limit);

  const handleDeliveryPincode = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    category: string
  ) => {
    event.target.value = onlyNumber(event, 6);
    if (category === "billing_address") {
      setBillingPincode(event.target.value);
    } else {
      setPincode(event.target.value);
    }
    handleInputChange(event, category, "postal_code");
  };

  useEffect(() => {
    const newFormData = { ...finalForm };

    newFormData.receiver_address.city = city;
    newFormData.receiver_address.state = state;

    setFinalForm(newFormData);
  }, [city, state]);

  useEffect(() => {
    const newFormData = { ...finalForm };

    if (typeof newFormData.billing_address === "object") {
      newFormData.billing_address.city = billingCity;
      newFormData.billing_address.state = billingState;
    }

    setFinalForm(newFormData);
  }, [billingCity, billingState]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    category: string,
    field: string
  ) => {
    const newFormData = { ...finalForm };

    if (
      typeof newFormData[category] === "object" &&
      newFormData[category] !== null
    ) {
      (newFormData[category] as { [key: string]: string })[field] =
        e.target.value;
    }
    setFinalForm(newFormData);
  };

  const buttonNext = () => {
    handleNext();
  };

  const [canNext, setCanNext] = useState(true);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.target.value = onlyNumber(event, 10);

    handleInputChange(event, "receiver_contact", "mobile_number");
  };

  const [billing, setBilling] = useState(true);

  const handleSameBilling = (
    _e: ChangeEvent<HTMLInputElement>,
    value: boolean
  ) => {
    setBilling(value);
    setBillingPincode("000000");

    if (!value) {
      setFinalForm({
        ...finalForm,
        ...{
          billing_address: {
            address: "",
            postal_code: "",
            state: "",
            city: "",
          },
        },
      });
    } else {
      setFinalForm({
        ...finalForm,
        ...{
          billing_address: "receiver_address",
        },
      });
    }
  };

  const allReciverAddressValidation = (): boolean => {
    return (
      finalForm.receiver_address.address.trim() !== "" &&
      finalForm.receiver_address.postal_code !== "" &&
      finalForm.receiver_contact.company_name.trim() !== "" &&
      finalForm.receiver_contact.email.trim() !== "" &&
      finalForm.receiver_contact.mobile_number.trim() !== "" &&
      finalForm.receiver_contact.person_name.trim() !== "" &&
      finalForm.receiver_address.state.trim() !== "" &&
      finalForm.receiver_address.city.trim() !== "" &&
      ifValidEmail(finalForm.receiver_contact.email)
    );
  };

  const allBillingAddressValidation = (): boolean => {
    let canNext =
      typeof finalForm.billing_address === "object"
        ? finalForm.billing_address.address.trim() !== "" &&
          finalForm.billing_address.postal_code !== "" &&
          finalForm.billing_address.city.trim() !== "" &&
          finalForm.billing_address.state.trim() !== ""
        : true;
    return canNext;
  };

  const validateForm = () =>
    setCanNext(allReciverAddressValidation() && allBillingAddressValidation());

  const [emailError, setEmailError] = useState<boolean>(false);

  const ifValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const checkEmailAtEveryStep = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const isValidEmail = ifValidEmail(e.target.value);
    const newFormData = { ...finalForm };
    newFormData.receiver_contact.email = e.target.value;
    setFinalForm(newFormData);
    isValidEmail ? setEmailError(false) : setEmailError(true);
  };

  useEffect(() => {
    validateForm();
  }, [billing, finalForm, billingCity, billingState, city, state]);

  return (
    <>
      <Title title={`Add ${label}`} />
      <Box
        sx={{
          margin: "10px 0",
        }}
      >
        <Typography marginBottom={2}>
          To whom is the order being delivered? (Buyer's Info)
        </Typography>
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <FormLabel>
                <Typography fontSize={12}>Mobile Number</Typography>
              </FormLabel>
              <StyledInput
                value={finalForm.receiver_contact.mobile_number}
                placeholder="Enter buyer's phone number"
                fullWidth
                onChange={(e) => handleChange(e)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormLabel>
                <Typography fontSize={12}>Full Name</Typography>
              </FormLabel>
              <StyledInput
                value={finalForm.receiver_contact.person_name}
                placeholder="Enter full name"
                fullWidth
                onChange={(e) =>
                  handleInputChange(e, "receiver_contact", "person_name")
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormLabel>
                <Typography fontSize={12}>Email Id</Typography>
              </FormLabel>
              <StyledInput
                value={finalForm.receiver_contact.email}
                placeholder="abc@gmail.com"
                fullWidth
                onChange={(e) => checkEmailAtEveryStep(e)}
              />
              {emailError && (
                <Typography color={"red"}>
                  Please provide valid email
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormLabel>
                <Typography fontSize={12}>Buyer's Company Name</Typography>
              </FormLabel>
              <StyledInput
                placeholder="Enter Buyer's Company Name"
                fullWidth
                value={finalForm.receiver_contact.company_name}
                onChange={(e) =>
                  handleInputChange(e, "receiver_contact", "company_name")
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography>
                Where is the order being delivered to? (Buyer's Address)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <FormLabel>
                <Typography fontSize={12}>Complete Address</Typography>
              </FormLabel>
              <StyledInput
                value={finalForm.receiver_address.address}
                placeholder="House/Floor No. Building Name or Street, Locality"
                fullWidth
                onChange={(e) =>
                  handleInputChange(e, "receiver_address", "address")
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormLabel>
                <Typography fontSize={12}>Pincode</Typography>
              </FormLabel>
              <StyledInput
                onChange={(e) => handleDeliveryPincode(e, "receiver_address")}
                placeholder="Enter Buyer's Pincode"
                fullWidth
                value={finalForm.receiver_address.postal_code}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormLabel>
                <Typography fontSize={12}>City</Typography>
              </FormLabel>
              <StyledInput
                value={finalForm.receiver_address.city}
                placeholder="Enter Buyer's City"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormLabel>
                <Typography fontSize={12}>State</Typography>
              </FormLabel>
              <StyledInput
                value={finalForm.receiver_address.state}
                placeholder="Please Select State"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
              >
                <Checkbox
                  defaultChecked
                  size={"small"}
                  onChange={handleSameBilling}
                />
                <Typography fontSize={12}>
                  Billing address is same as the shipping address
                </Typography>
              </Stack>
            </Grid>
            {!billing && (
              <>
                <Grid item xs={12} sm={12} md={6}>
                  <FormLabel>
                    <Typography fontSize={12}>Complete Address</Typography>
                  </FormLabel>
                  <StyledInput
                    value={
                      typeof finalForm.billing_address === "object"
                        ? finalForm.billing_address.address
                        : ""
                    }
                    placeholder="House/Floor No. Building Name or Street, Locality"
                    fullWidth
                    onChange={(e) =>
                      handleInputChange(e, "billing_address", "address")
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormLabel>
                    <Typography fontSize={12}>Pincode</Typography>
                  </FormLabel>
                  <StyledInput
                    onChange={(e) =>
                      handleDeliveryPincode(e, "billing_address")
                    }
                    placeholder="Enter Buyer's Pincode"
                    fullWidth
                    value={
                      typeof finalForm.billing_address === "object"
                        ? finalForm.billing_address.postal_code
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormLabel>
                    <Typography fontSize={12}>City</Typography>
                  </FormLabel>
                  <StyledInput
                    value={
                      typeof finalForm.billing_address === "object"
                        ? finalForm.billing_address.city
                        : ""
                    }
                    placeholder="Enter Buyer's City"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormLabel>
                    <Typography fontSize={12}>State</Typography>
                  </FormLabel>
                  <StyledInput
                    value={
                      typeof finalForm.billing_address === "object"
                        ? finalForm.billing_address.state
                        : ""
                    }
                    placeholder="Please Select State"
                    fullWidth
                    disabled
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>

      <Stack
        flexDirection={"row"}
        justifyContent={"flex-end"}
        alignItems={"center"}
        // marginTop={2}
        marginTop={2}
      >
        <StyledButton
          disabled={!canNext}
          variant="contained"
          sx={{
            marginBottom: "60px",
          }}
          onClick={buttonNext}
        >
          Next
        </StyledButton>
      </Stack>
    </>
  );
};

export default BuyerDetailsForm;
